import React, { useCallback, useEffect, useRef } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { TriggerProps } from '@atlaskit/popup/types';

import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import {
	PAGE_REMIX_OPTIONS,
	getTypeDisplayName,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/utils';
import remixOverlayMessages from '@atlassian/malleable-ui-components/remix/remix-overlay/messages';
import type {
	RemixOptionSelectionParameters,
	RemixOptionKind,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/types';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { DynamicUiSource } from './common-types.ts';
import { flattenDocContentForPageRemixFragment } from './flattenDocContentForPageRemixFragment.ts';
import { useRemixThinkingBarProgress } from './useRemixThinkingBarProgress.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';
import type { RemixContentComponentProps } from '@atlassian/malleable-ui-components/types';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

const PAGE_REMIX_ENTRY_POINT = 'objectSidebar';
const PAGE_REMIX_SOURCE = 'remixModalFromObjectLevelSidebar';
const PAGE_REMIX_OBJECT_CONTENT_TYPES: Partial<Record<RemixOptionKind, string>> = {
	slides: 'slides',
	whiteboard: 'whiteboard',
	database: 'database',
};

const getPageRemixObjectCreationContext = (type: RemixOptionKind) => {
	if (type === 'infographic') {
		return { experience: 'remix_object' as const, blocks: ['infographic'] };
	}

	const contentType = PAGE_REMIX_OBJECT_CONTENT_TYPES[type];
	return contentType
		? { experience: 'remix_object' as const, contentTypes: [contentType] }
		: undefined;
};

const constructPageDynamicUiSource = (adfDocument: DocNode): DynamicUiSource => ({
	contentType: 'page' as const,
	startLocalId: '',
	endLocalId: '',
	startIndex: 0,
	endIndex: -1,
	fragmentAdf: flattenDocContentForPageRemixFragment(adfDocument),
	fullAdf: adfDocument,
});

/** Remix types that open the AIFC staging modal directly, skipping the thinking bar. */
const OPEN_AIFC_MODAL_TYPES = new Set(['slides', 'whiteboard', 'database']);

/**
 * Maps page-level remix types to the forcedContentType value expected by the
 * backend.
 */
const PAGE_REMIX_FORCED_CONTENT_TYPE: Record<string, string> = {
	slides: 'slide',
	whiteboard: 'whiteboard',
	database: 'database',
};

export type PageRemixModalProps = {
	adfDocument: DocNode | Promise<DocNode>;
	interactionSource?: InteractionSource;
	onClose: () => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
	isEphemeralViewVisible: boolean;
	/** Render function for the trigger element that the popup anchors to. */
	trigger?: (triggerProps: TriggerProps) => React.ReactNode;
	/** Popup placement relative to the trigger. Defaults to 'left'. */
	popupPlacement?: 'left-end';
	/** Popup offset from the trigger in [along, away] px. */
	popupOffset?: [number, number];
	/** Whether the popup is open. */
	isPopupOpen?: boolean;
	/** When this value changes the popup will reposition itself. */
	popupRepositionKey?: number;
	presentation?: 'popup' | 'modal';
	/**
	 * Called when the sidebar should open with a specific conversation.
	 * Must be invoked from outside the ConfluenceConversationAssistant
	 * wrapper so the chat-open event reaches the sidebar's store.
	 */
	onOpenSidebarConversation?: (conversationId: string) => void;
};

export const PageRemixModal = ({
	adfDocument,
	interactionSource = 'remixModalFromObjectLevelSidebar',
	onClose,
	onStopThinking,
	isEphemeralViewVisible,
	trigger,
	popupPlacement,
	popupOffset,
	isPopupOpen,
	popupRepositionKey,
	presentation = 'popup',
	onOpenSidebarConversation,
}: PageRemixModalProps): React.JSX.Element => {
	const isRemixCancelled = useRef(false);
	const isDirectOpen = useRef(false);
	const activeAdfRequestId = useRef(0);
	const openAIFCStagingModalRef = useRef<() => void>(() => {});

	const cancelActiveRemixRequest = useCallback(() => {
		isRemixCancelled.current = true;
		activeAdfRequestId.current += 1;
	}, []);

	const experienceTracker = useAIMateExperienceTracker();

	const openModalAndCloseOverlay = useCallback(() => {
		openAIFCStagingModalRef.current();
		onStopThinking();
		onClose();
	}, [onClose, onStopThinking]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		signalActionReady,
		resetCompletion,
	} = useRemixThinkingBarProgress({ onOpenPreview: openModalAndCloseOverlay });

	const onActionReady = useCallback(() => {
		// For types in OPEN_AIFC_MODAL_TYPES, the modal is
		// already open — skip reopening it via onActionReady.
		if (isRemixCancelled.current || isDirectOpen.current) {
			return;
		}
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		signalActionReady();
	}, [signalActionReady, experienceTracker]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		openAIFCStagingModal,
		closeAIFCStagingModal,
	} = useRemixToAIFC({ onActionReady, stagingAreaTargetMode: 'generating-from-content' });
	openAIFCStagingModalRef.current = openAIFCStagingModal;

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			activeAdfRequestId.current += 1;
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
		};
	}, []);

	const handleClose = useCallback(() => {
		cancelActiveRemixRequest();
		onClose();
	}, [cancelActiveRemixRequest, onClose]);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (active) {
				isRemixCancelled.current = false;
				isDirectOpen.current = false;
				// Clear stale completion state from any previous flow.
				resetCompletion();
			} else {
				cancelActiveRemixRequest();
				isDirectOpen.current = false;
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User cancelled remix thinking',
					attributes: { reason: 'User cancelled remix thinking' },
				});
				stopRemixing();
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: {
						entryPoint: PAGE_REMIX_ENTRY_POINT,
						remixSource: 'cancelledRemixThinking',
						originalRemixSource: PAGE_REMIX_SOURCE,
						product: 'confluence',
					},
				});
				onStopThinking({ reopenModal: true });
			}
		},
		[stopRemixing, onStopThinking, experienceTracker, resetCompletion, cancelActiveRemixRequest],
	);

	const prevIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error('Rovo agent error during remix'),
			});
			onStopThinking({ reopenModal: true });
		}
		prevIsRemixError.current = isRemixError;
	}, [isRemixError, onStopThinking, experienceTracker]);

	useEffect(() => {
		if (!isRemixCancelled.current && isRemixError) {
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			onStopThinking();
			onClose();
		}
	}, [isRemixError, isEphemeralViewVisible, closeAIFCStagingModal, onStopThinking, onClose]);

	// Dismiss the thinking bar and close the popup when the user stops the remix
	// from the Rovo side chat.
	useEffect(() => {
		if (isRemixAborted && !isRemixCancelled.current) {
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			onStopThinking();
			onClose();
		}
	}, [isRemixAborted, isEphemeralViewVisible, closeAIFCStagingModal, onStopThinking, onClose]);

	const handleOptionSelect = useCallback(
		async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
			const isAwaitingAdf = adfDocument instanceof Promise;
			const adfRequestId = activeAdfRequestId.current + 1;
			activeAdfRequestId.current = adfRequestId;

			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { remixType: type, remixSubtype: subType, includesFreegenPrompt: !!userPrompt },
			});

			let resolvedAdfDocument: DocNode;
			try {
				resolvedAdfDocument = isAwaitingAdf ? await adfDocument : adfDocument;
			} catch (error) {
				if (activeAdfRequestId.current !== adfRequestId) {
					return;
				}

				experienceTracker.fail({
					name: ExperienceName.REMIX_START,
					error: error instanceof Error ? error : new Error('Failed to load content for Remix'),
				});
				onStopThinking();
				handleClose();
				return;
			}

			if (activeAdfRequestId.current !== adfRequestId) {
				return;
			}

			const dynamicUiSource = constructPageDynamicUiSource(resolvedAdfDocument);
			const forcedContentType = PAGE_REMIX_FORCED_CONTENT_TYPE[type];
			const additionalCreationContextParams = getPageRemixObjectCreationContext(type);
			const promptText =
				type === 'slides' ? `Remix the selected content as ${getTypeDisplayName(type)}` : undefined;

			if (!OPEN_AIFC_MODAL_TYPES.has(type)) {
				isDirectOpen.current = true;
				void sendAIFCPrompt({
					userPrompt,
					promptText,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					dynamicUiSource,
					interactionSource,
					additionalCreationContextParams,
				}).then((conversationId) => {
					if (conversationId && !isRemixCancelled.current) {
						onOpenSidebarConversation?.(conversationId);
					}
				});
				experienceTracker.succeed({ name: ExperienceName.REMIX_START });
				onClose();
			} else {
				isDirectOpen.current = true;
				void sendAIFCPrompt({
					userPrompt,
					promptText,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					dynamicUiSource,
					interactionSource,
					forcedContentType,
					additionalCreationContextParams,
				}).then(() => {
					if (!isRemixCancelled.current) {
						experienceTracker.succeed({ name: ExperienceName.REMIX_START });
						openAIFCStagingModal({ forceFullModal: true });
					}
				});
				onClose();
			}
		},
		[
			sendAIFCPrompt,
			adfDocument,
			interactionSource,
			openAIFCStagingModal,
			onClose,
			onStopThinking,
			experienceTracker,
			onOpenSidebarConversation,
			handleClose,
		],
	);

	return (
		<RemixContainer
			onClose={handleClose}
			onOptionSelect={handleOptionSelect}
			onRemixFlowActiveChange={onRemixFlowActiveChange}
			isEphemeralViewVisible={isEphemeralViewVisible}
			skipThinkingTypes={new Set([...OPEN_AIFC_MODAL_TYPES, 'infographic', 'custom'])}
			options={PAGE_REMIX_OPTIONS}
			subtitle={remixOverlayMessages.pageRemixSubtitle}
			presentation={presentation}
			trigger={trigger}
			popupPlacement={popupPlacement}
			popupOffset={popupOffset}
			isPopupOpen={isPopupOpen}
			popupRepositionKey={popupRepositionKey}
			showProgressBar={showProgressBar}
			isComplete={isComplete}
			onCompleteAnimationFinished={onCompleteAnimationFinished}
		/>
	);
};

export const EditorPageRemixModal = (
	props: Extract<RemixContentComponentProps, { remixType: 'object' }>,
) => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const interactionSource = props.remixModalMetadata?.interactionSource;

	return (
		<PageRemixModal
			adfDocument={props.adfDocument ?? { type: 'doc', version: 1, content: [] }}
			onClose={props.handleModalClose}
			onStopThinking={props.onStopThinking}
			isEphemeralViewVisible={showSmartCreationModal}
			interactionSource={interactionSource}
			presentation="modal"
		/>
	);
};
